import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const normalizeMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (typeof window === 'undefined') return url;

  // If it's a base64 string, return as is
  if (url.startsWith('data:')) return url;

  // If it's a relative path, prepend API base URL
  let finalUrl = url;
  if (url.startsWith('/uploads/') || url.startsWith('uploads/')) {
    const relativePath = url.startsWith('/') ? url : `/${url}`;
    finalUrl = `${API_BASE_URL.replace('/api', '')}${relativePath}`;
  }

  try {
    const current = window.location;
    const parsed = new URL(finalUrl, current.origin);

    // Only force protocol for http/https URLs, not for data: or others
    if (parsed.protocol.startsWith('http') && parsed.protocol !== current.protocol) {
      parsed.protocol = current.protocol;
      return parsed.toString();
    }
    return parsed.toString();
  } catch (e) {
    // Fallback for non-URL strings or failed parsing
    return finalUrl;
  }
};

const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
};

export const getAudios = async () => {
  const res = await axios.get(`${API_BASE_URL}/audios`);
  const list = Array.isArray(res.data) ? res.data : [];
  return list.map((a) => ({
    ...a,
    audioSource: normalizeMediaUrl(a.audioSource)
  }));
};

export const saveAudio = async (audio) => {
  const id = audio.id;
  
  // Fast path for new uploads using FormData (avoids Base64)
  if (!id && audio.audioFile) {
    const formData = new FormData();
    formData.append('audio', audio.audioFile);
    if (audio.title) formData.append('title', audio.title);
    if (audio.artist) formData.append('artist', audio.artist);
    if (audio.category) formData.append('category', audio.category);
    if (audio.duration) formData.append('duration', audio.duration);
    if (audio.desc) formData.append('desc', audio.desc);
    
    // /upload route now returns the full created object
    const res = await axios.post(`${API_BASE_URL}/audios/upload`, formData, {
      headers: {
        // Let axios set the content-type with boundary
      },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      timeout: 0
    });
    return res.data;
  }

  // Fallback / Edit path (Base64)
  let audioSource = audio.audioSource || '';

  if (audio.audioFile) {
    audioSource = await readFileAsBase64(audio.audioFile);
  }

  const payload = {
    title: audio.title || '',
    artist: audio.artist || '',
    category: audio.category || '',
    duration: audio.duration || '',
    desc: audio.desc || '',
    audioSource,
    hasLocalAudio: !!audioSource
  };

  if (id) {
    const res = await axios.put(`${API_BASE_URL}/audios/${id}`, payload);
    return res.data;
  }

  const res = await axios.post(`${API_BASE_URL}/audios`, payload);
  return res.data;
};

export const deleteAudio = async (id) => {
  if (!id) return;
  await axios.delete(`${API_BASE_URL}/audios/${id}`);
};

export const getAudioFile = async (id) => {
  if (!id) return null;
  const list = await getAudios();
  const found = list.find((a) => a.id === id || a.id === String(id));
  if (!found || !found.audioSource) {
    return null;
  }
  const src = normalizeMediaUrl(found.audioSource);
  const response = await fetch(src);
  const blob = await response.blob();
  return blob;
};

export const saveAudioFile = saveAudio;
export const deleteAudioFile = deleteAudio;
